import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import { useNavigate } from "react-router-dom";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableProps } from "antd";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { SharedContextProps } from "@lib/types/Context";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  apiAddProviderTransactionConfig,
  apiDeleteProviderTransactionConfig,
  apiGetProviderTransactionConfigs,
  apiUpdateProviderTransactionConfig,
} from "@lib/api/provider";
import { PaymentTransactionConfigModal } from "@lib/components/PageComponents/PaymentTransactionConfig";
import { ProviderTransactionConfig } from "@lib/types";
import { useTypedSelector } from "@lib/redux";

const SharedPaymentTransactionConfigs: React.FC<SharedContextProps> = ({
  context,
  customTitle,
  detailContext,
  withoutContainer = false,
  pageIsActive = true,
  providerId,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { role } = useTypedSelector((state) => state.auth?.user);

  const initialFilters = [
    ...(providerId ? [`providerId||$eq||${providerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    join: ["provider"],
    sort: "createdAt,DESC",
    filter: initialFilters,
  });

  const query = useQuery({
    queryFn: () => apiGetProviderTransactionConfigs(params),
    queryKey: ["provider-transaction-configs", JSON.stringify(params)],
    enabled: !!params && pageIsActive,
    refetchInterval: 1000 * 60,
  });

  const { data, isLoading, isFetching } = query;
  const apiData = data?.data?.data;

  const addMutation = useMutation({
    mutationFn: apiAddProviderTransactionConfig,
    mutationKey: ["provider-transaction-configs"],
    onSuccess: (data) => {
      query.refetch();
      handleSelect(data.data);
    },
  });
  const updateMutation = useMutation({
    mutationFn: apiUpdateProviderTransactionConfig,
    mutationKey: ["provider-transaction-configs"],
    onSuccess: () => {
      query.refetch();
    },
  });
  const deleteMutation = useMutation({
    mutationFn: apiDeleteProviderTransactionConfig,
    mutationKey: ["provider-transaction-configs"],
    onSuccess: () => {
      query.refetch();
    },
  });

  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<ProviderTransactionConfig>();

  useEffect(() => {
    if (!openModal) setSelected(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (item: ProviderTransactionConfig) => {
    setSelected(item);
    toggle(true);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        handleSelect,
        navigate,
        deleteQuery: deleteMutation,
        openModal,
        messages,
        apiData,
        userRole: role,
      }),
    [apiData, params],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["providerId"];
    }
    return [];
  }, [detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params]);

  const title =
    customTitle || (messages["common.paymentTransactionConfigs"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={query}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          onChange={onChange}
          rowKey={"id"}
          loading={isLoading || isFetching}
          minusHeight={minusHeight}
        />
      </AppsContent>

      <PaymentTransactionConfigModal
        openModal={openModal}
        toggleModal={toggle}
        mutationQuery={selected ? updateMutation : addMutation}
        selected={selected}
      />
    </AppsContainer>
  );
};

export default SharedPaymentTransactionConfigs;
